// client/src/utils/errors.ts

/**
 * Normalizes any error value into a human-readable string message.
 * Handles various error formats: Axios errors, standard Errors, plain objects, etc.
 * 
 * @param e - The error value (can be any type)
 * @returns A string representation of the error suitable for display
 */
export function getErrorMessage(e: any): string {
  // Handle null/undefined
  if (!e) return 'Unknown error';

  // Handle Axios-style errors with response data
  if (e?.response?.data) {
    const data = e.response.data;
    
    // Check for common error message fields
    if (typeof data.error === 'string' && data.error) {
      return data.error;
    }
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }
    if (typeof data.msg === 'string' && data.msg) {
      return data.msg;
    }
    
    // If data itself is a string, return it
    if (typeof data === 'string') {
      return data;
    }
  }

  // Handle standard Error objects
  if (e instanceof Error && e.message) {
    return e.message;
  }

  // Handle plain error message property
  if (typeof e.message === 'string' && e.message) {
    return e.message;
  }

  // Handle strings
  if (typeof e === 'string') {
    return e;
  }

  // Last resort: try to stringify
  try {
    const str = JSON.stringify(e);
    // Don't return empty objects/arrays
    if (str === '{}' || str === '[]') {
      return 'Unknown error';
    }
    return str;
  } catch {
    // If stringification fails, convert to string
    return String(e);
  }
}

/**
 * Extracts HTTP status code from an error if available
 */
export function getErrorStatus(e: any): number | null {
  if (e?.response?.status) {
    return e.response.status;
  }
  if (e?.status) {
    return e.status;
  }
  return null;
}

/**
 * Checks if an error is an authentication error (401)
 */
export function isAuthError(e: any): boolean {
  return getErrorStatus(e) === 401;
}

/**
 * Checks if an error is a forbidden error (403)
 */
export function isForbiddenError(e: any): boolean {
  return getErrorStatus(e) === 403;
}

/**
 * Safely renders any value as a React-safe string.
 * Use this when you need to display a value that might be an object.
 */
export function toDisplayString(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}


